import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../utils/apiConfig";
import {
  X,
  Shield,
  ShieldCheck,
  Mail,
  Phone,
  Smartphone,
  Key,
  CheckCircle,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";

const TwoFactorAuth = ({ isOpen, onClose, standalone = false, onSuccess }) => {
  const { t } = useTranslation();
  const [twoFAStatus, setTwoFAStatus] = useState({
    enabled2FA: "false",
    type2FA: null,
    verified2FA: "false",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [step, setStep] = useState("status"); // 'status', 'setup', 'verify'
  const [selectedType, setSelectedType] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showSecret, setShowSecret] = useState(false);

  // Get token
  const getToken = () => {
    return localStorage.getItem("token") || sessionStorage.getItem("token");
  };

  // Fetch 2FA status
  const fetch2FAStatus = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/auth/2fa-status`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      if (response.ok) {
        setTwoFAStatus(data.data);
      } else {
        setError(data.message || t("twoFactorAuth.errors.failedToFetchStatus"));
      }
    } catch (err) {
      setError(t("twoFactorAuth.errors.networkErrorFetch"));
    } finally {
      setLoading(false);
    }
  };

  // Toggle 2FA
  const toggle2FA = async (enable, type = null) => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/auth/toggle-2fa`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          enable: enable.toString(),
          type2FA: type,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        if (enable && data.data) {
          if (data.data.qrCode) {
            setQrCode(data.data.qrCode);
            setSecret(data.data.secret);
          }
          setStep("verify");
        } else {
          // 2FA disabled
          setTwoFAStatus({
            enabled2FA: "false",
            type2FA: null,
            verified2FA: "false",
          });
          setStep("status");
        }
      } else {
        setError(data.message || t("twoFactorAuth.errors.failedToToggle"));
      }
    } catch (err) {
      setError(t("twoFactorAuth.errors.networkErrorToggle"));
    } finally {
      setLoading(false);
    }
  };

  // Generate 2FA code for EMAIL/PHONE
  const generate2FACode = async () => {
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/auth/generate-2fa-code`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
      } else {
        setError(data.message || t("twoFactorAuth.errors.failedToGenerateCode"));
      }
    } catch (err) {
      setError(t("twoFactorAuth.errors.networkErrorGenerate"));
    } finally {
      setLoading(false);
    }
  };

  // Verify 2FA code
  const verify2FACode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError(t("twoFactorAuth.errors.enterCompleteCode"));
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          code: verificationCode,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(data.message);
        setTwoFAStatus((prev) => ({
          ...prev,
          verified2FA: "true",
        }));
        setTimeout(() => {
          setStep("status");
          setVerificationCode("");
          setQrCode("");
          setSecret("");
          // Call onSuccess callback if provided
          if (onSuccess) {
            onSuccess();
          }
        }, 1500);
      } else {
        setError(data.message || t("twoFactorAuth.errors.failedToVerify"));
      }
    } catch (err) {
      setError(t("twoFactorAuth.errors.networkErrorVerify"));
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    // Only allow numeric characters and limit to 6 digits
    if (/^\d*$/.test(value) && value.length <= 6) {
      setVerificationCode(value);
    }
    if (error) setError("");
  };

  // Copy secret to clipboard
  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setMessage(t("twoFactorAuth.errors.secretCopied"));
      setTimeout(() => setMessage(""), 2000);
    } catch (err) {
      setError(t("twoFactorAuth.errors.failedToCopy"));
    }
  };

  // Load 2FA status on component mount
  useEffect(() => {
    if (isOpen || standalone) {
      fetch2FAStatus();
      setStep("status");
      setError("");
      setMessage("");
      setVerificationCode("");
      setQrCode("");
      setSecret("");
    }
  }, [isOpen, standalone]);

  if (!isOpen && !standalone) return null;

  const handleSetup = (type) => {
    setSelectedType(type);
    setStep("setup");
    setError("");
    setMessage("");
  };

  const ContentWrapper = ({ children }) => {
    if (standalone) {
      return (
        <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-sm">
          <div className="space-y-6">{children}</div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="p-8 space-y-6">{children}</div>
        </div>
      </div>
    );
  };

  const renderStatusView = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="mx-auto w-20 h-20 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
          {twoFAStatus.enabled2FA === "true" ? (
            <ShieldCheck className="w-10 h-10 text-green-500" />
          ) : (
            <Shield className="w-10 h-10 text-blue-500" />
          )}
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          {t("twoFactorAuth.title")}
        </h3>
        <p className="text-sm text-gray-500">
          {twoFAStatus.enabled2FA === "true"
            ? t("twoFactorAuth.twoFAEnabled", { type: twoFAStatus.type2FA?.replace("_", " ") })
            : t("twoFactorAuth.addExtraSecurity")}
        </p>
      </div>

      {twoFAStatus.enabled2FA === "true" ? (
        <div className="space-y-4">
          <div className="bg-green-50/50 border-2 border-green-100 rounded-xl p-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <span className="text-green-800 font-medium block">
                  {t("twoFactorAuth.twoFAIsActive")}
                </span>
                <span className="text-green-600 text-sm">
                  {t("twoFactorAuth.using", { type: twoFAStatus.type2FA?.replace("_", " ") })}
                </span>
              </div>
            </div>
            {twoFAStatus.verified2FA === "false" && (
              <div className="flex items-center mt-3 bg-orange-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-500 mr-2" />
                <p className="text-sm text-orange-700">
                  {t("twoFactorAuth.completeVerification")}
                </p>
              </div>
            )}
          </div>

          {twoFAStatus.verified2FA === "false" && (
            <button
              onClick={() => {
                if (twoFAStatus.type2FA === "GOOGLE_AUTH") {
                  setStep("verify");
                } else {
                  generate2FACode();
                  setStep("verify");
                }
              }}
              className="w-full bg-orange-500 text-white py-3 px-4 rounded-xl hover:bg-orange-600 transition-colors font-medium shadow-sm"
              disabled={loading}
            >
              {t("twoFactorAuth.completeVerificationButton")}
            </button>
          )}

          <button
            onClick={() => toggle2FA(false)}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
            disabled={loading}
          >
            {loading ? t("twoFactorAuth.disabling") : t("twoFactorAuth.disable2FA")}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 text-center">
            {t("twoFactorAuth.chooseMethod")}
          </p>

          <div className="grid gap-4">
            <button
              onClick={() => handleSetup("GOOGLE_AUTH")}
              className="flex items-center p-4 border-2 border-gray-100 rounded-xl hover:border-blue-100 hover:bg-blue-50/50 transition-all group"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-100 transition-colors">
                <Smartphone className="w-6 h-6 text-blue-500" />
              </div>
              <div className="text-left flex-1">
                <div className="font-semibold text-gray-800">
                  {t("twoFactorAuth.authenticatorApp")}
                </div>
                <div className="text-sm text-gray-500">
                  {t("twoFactorAuth.authenticatorAppDesc")}
                </div>
              </div>
            </button>

            <button
              onClick={() => handleSetup("EMAIL")}
              className="flex items-center p-4 border-2 border-gray-100 rounded-xl hover:border-green-100 hover:bg-green-50/50 transition-all group"
            >
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-100 transition-colors">
                <Mail className="w-6 h-6 text-green-500" />
              </div>
              <div className="text-left flex-1">
                <div className="font-semibold text-gray-800">{t("twoFactorAuth.email")}</div>
                <div className="text-sm text-gray-500">
                  {t("twoFactorAuth.emailDesc")}
                </div>
              </div>
            </button>

            {/* <button
              onClick={() => handleSetup("PHONE")}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Phone className="w-6 h-6 text-purple-600 mr-3" />
              <div className="text-left">
                <div className="font-medium text-gray-800">SMS</div>
                <div className="text-sm text-gray-600">
                  Receive codes via SMS
                </div>
              </div>
            </button> */}
          </div>
        </div>
      )}
    </div>
  );

  const renderSetupView = () => (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => setStep("status")}
          className="text-gray-500 hover:text-gray-700 mr-3"
        >
          ←
        </button>
        <h3 className="text-lg font-semibold text-gray-800">
          {t("twoFactorAuth.setupTitle", { type: selectedType?.replace("_", " ") })}
        </h3>
      </div>

      <div className="text-center">
        <p className="text-sm text-gray-600 mb-4">
          {selectedType === "GOOGLE_AUTH"
            ? t("twoFactorAuth.setupGoogleAuth")
            : t("twoFactorAuth.setupEmailPhone", { type: selectedType?.toLowerCase() })}
        </p>

        <button
          onClick={() => toggle2FA(true, selectedType)}
          className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          style={{
            background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
            borderRadius: "10px"
          }}
          disabled={loading}
        >
          {loading
            ? t("twoFactorAuth.settingUp")
            : t("twoFactorAuth.enable2FA", { type: selectedType?.replace("_", " ") })}
        </button>
      </div>
    </div>
  );

  const renderVerifyView = () => (
    <div className="space-y-8">
      <div className="flex items-center">
        <button
          onClick={() =>
            twoFAStatus.enabled2FA === "true"
              ? setStep("status")
              : setStep("setup")
          }
          className="text-gray-500 hover:text-gray-700 mr-3"
        >
          ←
        </button>
        <h3 className="text-lg font-semibold text-gray-800">
          {t("twoFactorAuth.verifyTitle")}
        </h3>
      </div>

      {qrCode && (
        <div className="text-center space-y-4">
          <p className="text-sm text-gray-600">
            {t("twoFactorAuth.scanQRCode")}
          </p>
          <div className="bg-white p-4 rounded-lg border inline-block">
            <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
          </div>

          {secret && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                {t("twoFactorAuth.cantScan")}
              </p>
              <div className="flex items-center justify-center space-x-2">
                <code
                  className={`text-sm bg-white px-3 py-2 rounded border ${showSecret ? "" : "blur-sm"
                    }`}
                >
                  {secret}
                </code>
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  {showSecret ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={copySecret}
                  className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {(twoFAStatus.type2FA === "EMAIL" || twoFAStatus.type2FA === "PHONE") && (
        <div className="text-center">
          <button
            onClick={generate2FACode}
            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors mb-4"
            disabled={loading}
          >
            {loading
              ? t("twoFactorAuth.sending")
              : twoFAStatus.type2FA === "EMAIL" ? t("twoFactorAuth.sendCodeToEmail") : t("twoFactorAuth.sendCodeToPhone")}
          </button>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
            {t("twoFactorAuth.enterVerificationCode")}
          </label>

          {/* OTP Input Field */}
          <div className="max-w-xs mx-auto">
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={verificationCode}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-center font-mono text-lg tracking-wider"
                placeholder="000000"
                maxLength="6"
                pattern="[0-9]{6}"
                inputMode="numeric"
                required
                autoFocus
              />
            </div>
            <div className="text-center mt-2">
              <span className="text-sm text-gray-500">
                {t("twoFactorAuth.enter6DigitCode")}
              </span>
            </div>
          </div>
          {/* <div className="mb-6">
            <input
              type="text"
              value={verificationCode}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg font-mono"
              placeholder="000000"
              maxLength="6"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              disabled={loading}
            />
          </div> */}

          {/* Visual Code Display */}
          {/* <div className="text-center text-sm text-gray-500 mb-4">
            Code:{" "}
            <span className="font-mono">{verificationCode || "------"}</span>
          </div> */}
        </div>

        <button
          onClick={verify2FACode}
          style={{
            background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
            borderRadius: "10px"
          }}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          disabled={loading || verificationCode.length !== 6}
        >
          {loading ? t("twoFactorAuth.verifying") : t("twoFactorAuth.verifyCode")}
        </button>
      </div>
    </div>
  );

  return (
    <ContentWrapper>
      {/* Header */}
      {!standalone && (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
              <Key className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">
              {t("twoFactorAuth.title")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors rounded-lg p-2 hover:bg-gray-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {standalone && (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3">
            <Key className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">
            Two-Factor Authentication
          </h2>
        </div>
      )}

      {/* Messages */}
      {(error || message) && (
        <div className="space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center animate-fade-in">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {message && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center animate-fade-in">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{message}</span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {loading && step === "status" ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">{t("twoFactorAuth.loadingStatus")}</p>
        </div>
      ) : (
        <>
          {step === "status" && renderStatusView()}
          {step === "setup" && renderSetupView()}
          {step === "verify" && renderVerifyView()}
        </>
      )}
    </ContentWrapper>
  );
};

export default TwoFactorAuth;
